import type { BikeConfig } from '../config';

/** Why the rider went down. Every cause is a physical limit being exceeded (see checkLimits). */
export type FallCause = 'tipped' | 'washout' | 'spin' | 'landing' | 'overBars' | 'hit';

/**
 * What the step measured, for the limit checks (one reused object: no allocation at 240 Hz).
 * All of it is physics state: angles, tyre use, closing speeds. None of it is "the player did X".
 */
export interface LimitInput {
  upDot: number;          // bike up . world up
  pitch: number;          // rad, nose above the horizon
  frontOnly: boolean;     // only the front tyre is on the ground
  rearOnly: boolean;      // only the rear tyre is on the ground
  liftVsGround: number;   // rad, nose above the ground under the rear tyre (rear only)
  frontSlideTime: number; // s the front tyre has been past its grip peak while carrying a lean or a brake
  leaned: boolean;        // the slide above happened with lean (low-side) rather than bolt upright (locked front)
  spinTime: number;       // s the bike has been sideways past SPIN_OUT
  hitSpeed: number;       // m/s into a solid obstacle this step (0 none)
  bottomRate: number;     // m/s the suspension was still closing at while on its bump stop this step
  legsBottomRate: number; // m/s the rider's legs were still closing at while at the end of their travel (0 none)
}

export const newLimitInput = (): LimitInput => ({
  upDot: 1, pitch: 0, frontOnly: false, rearOnly: false, liftVsGround: 0, frontSlideTime: 0, leaned: false,
  spinTime: 0, hitSpeed: 0, bottomRate: 0, legsBottomRate: 0,
});

/**
 * The only place a fall is decided (row 17 of the MX physics spec: no "if X then crash" anywhere else; a test greps
 * src/sim for it). Each line compares a measured physical quantity with what the tyres, the suspension, or the
 * rider's body can take:
 * - tipped: the bike is lying past the angle a rider can hold up (or it has looped over backwards: rear only and the
 *   nose past loopPitch from the ground);
 * - overBars: on the front alone with the nose down past endoPitch: the rider's arms cannot hold him behind the bars;
 * - washout: the front tyre stayed past the top of its friction circle while carrying lean (or bolt upright with a
 *   locked front for longer): its side force cannot hold the lean;
 * - spin: the rear has come round past SPIN_OUT and stayed there;
 * - landing: the suspension hit its bump stop still closing faster than the rider's legs can absorb, or the legs
 *   themselves ran out of travel that fast;
 * - hit: a solid obstacle stopped the bike faster than the rider can hold on.
 */
export function checkLimits(m: LimitInput, cfg: BikeConfig, spinOutTime: number): FallCause | null {
  if (m.hitSpeed > cfg.collision.fallSpeed) return 'hit';
  if (m.bottomRate > cfg.riderAbsorbSpeed || m.legsBottomRate > cfg.riderAbsorbSpeed) return 'landing';
  if (m.frontOnly && m.pitch < -cfg.endoPitch) return 'overBars';
  if (m.rearOnly && m.liftVsGround > cfg.loopPitch) return 'tipped';
  if (m.frontSlideTime >= (m.leaned ? cfg.washoutTime : cfg.lockedFrontFallTime)) return 'washout';
  if (m.spinTime >= spinOutTime) return 'spin';
  if (m.upDot < cfg.fallUpDot) return 'tipped';
  return null;
}
